import { useCallback, useEffect, useMemo, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import { Link, useNavigate, useParams } from "react-router";
import { ChevronLeft, RefreshCw } from "lucide-react";
import { useAgreementData } from "./agreement-edit/useAgreementData";
import type { AgreementForEdit } from "./agreement-edit/useAgreementData";
import {
  OutlineRail,
  type OutlinePanel,
} from "./agreement-edit/OutlineRail";
import { BodyEditor } from "./agreement-edit/BodyEditor";
import { DocumentDesignOverridePanel } from "./agreement-edit/DocumentDesignOverridePanel";
import { SettingsPanel } from "./agreement-edit/SettingsPanel";
import { CloneAction } from "./agreement-edit/CloneAction";
import { DocumentDesignEditor } from "./defaults/DocumentDesignEditor";
import { HeaderZone } from "./defaults/HeaderZone";
import { FooterZone } from "./defaults/FooterZone";
import { MarginsZone } from "./defaults/MarginsZone";
import { CountersignerZone } from "./defaults/CountersignerZone";
import { SignatureBlockZone } from "./defaults/SignatureBlockZone";
import { PreviewPane } from "./defaults/PreviewPane";
import { useDirtyTracking } from "./defaults/useDirtyTracking";
import { usePreviewIntegration } from "./defaults/usePreviewIntegration";
import { useToast } from "../../components/ui/Toast";
import { sanitizeAgreementHtml } from "@sections/sanitize";
import { Tooltip } from "../../components/ui";

const UPDATE_AGREEMENT = gql`
  mutation UpdateAgreementInPortal(
    $id: String!
    $name: String
    $agreementType: String
    $agreementTypeId: String
    $state: String
    $jurisdictionKind: String
    $jurisdictionValue: String
    $isActive: Boolean
    $agreementHtml: String
    $simpleHtml: String
    $headerHtml: String
    $topLeftHtml: String
    $topRightHtml: String
    $footerHtml: String
    $countersignerName: String
    $countersignerSignatureKey: String
    $marginTop: Float
    $marginBottom: Float
    $marginLeft: Float
    $marginRight: Float
    $showFirstPageMargins: Boolean
    $signatureBlockHtml: String
    $signatoryPrefix: String
    $signatorySuffix: String
    $previewContentHash: String
  ) {
    updateAgreement(
      id: $id
      name: $name
      agreementType: $agreementType
      agreementTypeId: $agreementTypeId
      state: $state
      jurisdictionKind: $jurisdictionKind
      jurisdictionValue: $jurisdictionValue
      isActive: $isActive
      agreementHtml: $agreementHtml
      simpleHtml: $simpleHtml
      headerHtml: $headerHtml
      topLeftHtml: $topLeftHtml
      topRightHtml: $topRightHtml
      footerHtml: $footerHtml
      countersignerName: $countersignerName
      countersignerSignatureKey: $countersignerSignatureKey
      marginTop: $marginTop
      marginBottom: $marginBottom
      marginLeft: $marginLeft
      marginRight: $marginRight
      showFirstPageMargins: $showFirstPageMargins
      signatureBlockHtml: $signatureBlockHtml
      signatoryPrefix: $signatoryPrefix
      signatorySuffix: $signatorySuffix
      previewContentHash: $previewContentHash
    ) {
      id
      name
      agreementType
      agreementTypeId
      state
      jurisdictionKind
      jurisdictionValue
      isActive
      agreementHtml
      simpleHtml
      headerHtml
      topLeftHtml
      topRightHtml
      footerHtml
      countersignerName
      countersignerSignatureKey
      marginTop
      marginBottom
      marginLeft
      marginRight
      showFirstPageMargins
      signatureBlockHtml
      signatoryPrefix
      signatorySuffix
      previewImageUrl
      previewContentHash
      previewGeneratedAt
    }
  }
`;

interface FormValues {
  // Identity / status (don't trigger preview staleness)
  name: string;
  agreementType: string;
  agreementTypeId: string | null;
  jurisdictionKind: string;
  state: string;
  jurisdictionValue: string;
  isActive: boolean;
  // Body
  agreementHtml: string;
  simpleHtml: string;
  // Design overrides — null = inherit, non-null = override
  headerHtml: string | null;
  topLeftHtml: string | null;
  topRightHtml: string | null;
  footerHtml: string | null; // bottom-left margin
  countersignerName: string | null;
  countersignerSignatureKey: string | null;
  marginTop: number | null;
  marginBottom: number | null;
  marginLeft: number | null;
  marginRight: number | null;
  showFirstPageMargins: boolean | null;
  signatureBlockHtml: string | null;
  // Signature block wording — treated as CONTENT (triggers preview staleness)
  signatoryPrefix: string;
  signatorySuffix: string;
}

/** Check whether the jurisdiction fields are fully specified. */
function jurisdictionComplete(kind: string, stateVal: string, valueVal: string): boolean {
  if (kind === "national") return true;
  if (!stateVal) return false; // state/county/city/zip all need state
  if (kind === "state") return true;
  return valueVal.length > 0; // county/city/zip need jurisdictionValue
}

const SETTINGS_FIELDS = new Set<keyof FormValues>([
  "name",
  "agreementType",
  "agreementTypeId",
  "jurisdictionKind",
  "state",
  "jurisdictionValue",
  "isActive",
]);

function valuesFromAgreement(a: AgreementForEdit): FormValues {
  return {
    name: a.name,
    agreementType: a.agreementType,
    agreementTypeId: a.agreementTypeId,
    jurisdictionKind: a.jurisdictionKind ?? "national",
    state: a.state ?? "",
    jurisdictionValue: a.jurisdictionValue ?? "",
    isActive: a.isActive,
    agreementHtml: a.agreementHtml ?? "",
    simpleHtml: a.simpleHtml ?? "",
    headerHtml: a.headerHtml,
    topLeftHtml: a.topLeftHtml,
    topRightHtml: a.topRightHtml,
    footerHtml: a.footerHtml,
    countersignerName: a.countersignerName,
    countersignerSignatureKey: a.countersignerSignatureKey,
    marginTop: a.marginTop,
    marginBottom: a.marginBottom,
    marginLeft: a.marginLeft,
    marginRight: a.marginRight,
    showFirstPageMargins: a.showFirstPageMargins,
    signatureBlockHtml: a.signatureBlockHtml,
    signatoryPrefix: a.signatoryPrefix ?? "Client",
    signatorySuffix: a.signatorySuffix ?? "",
  };
}

function StatusPill({
  status,
  isStale,
}: {
  status: ReturnType<typeof usePreviewIntegration>["status"];
  isStale: boolean;
}) {
  let label = "No preview yet";
  let cls = "bg-canvas hair border border-ink-6 text-ink-4 [&_.dot]:bg-ink-5";
  if (status === "generating") {
    label = "Generating preview…";
    cls = "bg-tide-ss hair border border-tide/40 text-tide [&_.dot]:bg-tide";
  } else if (status === "failed") {
    label = "Preview failed";
    cls = "bg-rust-s/40 hair border border-rust/40 text-rust [&_.dot]:bg-rust";
  } else if (isStale || status === "stale") {
    label = "Preview out of date";
    cls = "bg-spark-s/40 hair border border-spark-s text-spark [&_.dot]:bg-spark";
  } else if (status === "ready") {
    label = "Preview up to date";
    cls = "bg-sage-s/40 hair border border-sage-s text-sage [&_.dot]:bg-sage";
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium rounded-full px-2 py-0.5 ${cls}`}
    >
      <span className="dot"></span>
      {label}
    </span>
  );
}

export function AgreementEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { agreement, tenant, loading, error, refetch } = useAgreementData(id);
  const [save, saveResult] = useMutation(UPDATE_AGREEMENT);

  const [active, setActive] = useState<OutlinePanel>("body");

  const tracking = useDirtyTracking<FormValues>({
    name: "",
    agreementType: "",
    agreementTypeId: null,
    jurisdictionKind: "national",
    state: "",
    jurisdictionValue: "",
    isActive: false,
    agreementHtml: "",
    simpleHtml: "",
    headerHtml: null,
    topLeftHtml: null,
    topRightHtml: null,
    footerHtml: null,
    countersignerName: null,
    countersignerSignatureKey: null,
    marginTop: null,
    marginBottom: null,
    marginLeft: null,
    marginRight: null,
    showFirstPageMargins: null,
    signatureBlockHtml: null,
    signatoryPrefix: "Client",
    signatorySuffix: "",
  });

  // Hydrate the form once when the agreement query resolves.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (!hydrated && agreement) {
      tracking.markSaved(valuesFromAgreement(agreement));
      setHydrated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreement, hydrated]);

  const buildPreviewPayload = useCallback(() => {
    const v = tracking.values;
    const data: Record<string, unknown> = {
      agreementHtml: v.agreementHtml,
    };
    if (v.headerHtml != null) data.headerHtml = v.headerHtml;
    if (v.topLeftHtml != null) data.topLeftHtml = v.topLeftHtml;
    if (v.topRightHtml != null) data.topRightHtml = v.topRightHtml;
    if (v.footerHtml != null) data.footerHtml = v.footerHtml;
    if (v.countersignerName != null) data.countersignerName = v.countersignerName;
    if (v.countersignerSignatureKey != null)
      data.countersignerSignatureKey = v.countersignerSignatureKey;
    if (v.marginTop != null) data.marginTop = v.marginTop;
    if (v.marginBottom != null) data.marginBottom = v.marginBottom;
    if (v.marginLeft != null) data.marginLeft = v.marginLeft;
    if (v.marginRight != null) data.marginRight = v.marginRight;
    if (v.showFirstPageMargins != null)
      data.showFirstPageMargins = v.showFirstPageMargins;
    if (v.signatureBlockHtml != null) data.signatureBlockHtml = v.signatureBlockHtml;
    data.signatoryPrefix = v.signatoryPrefix;
    data.signatorySuffix = v.signatorySuffix;
    return {
      agreementId: id ?? "",
      agreementData: data,
    };
  }, [tracking.values, id]);

  const preview = usePreviewIntegration({
    initialHash: agreement?.previewContentHash ?? null,
    initialPreviewUrl: agreement?.previewImageUrl ?? null,
    initialGeneratedAt: agreement?.previewGeneratedAt ?? null,
    idleMs: 20000,
    buildTriggerPayload: buildPreviewPayload,
  });

  // Refetch once the renderer reports ready so we pick up the new
  // hash + image URL written by WS3.
  useEffect(() => {
    if (preview.status === "ready" && preview.previewHash == null && hydrated) {
      void refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview.status]);

  // Mark stale only on content changes — settings-only edits don't
  // affect the rendered PDF, so they stay "preview is fine to save"
  // per the WS6 design.
  const contentSnapshot = useMemo(() => {
    const v = tracking.values;
    return JSON.stringify({
      agreementHtml: v.agreementHtml,
      simpleHtml: v.simpleHtml,
      headerHtml: v.headerHtml,
      topLeftHtml: v.topLeftHtml,
      topRightHtml: v.topRightHtml,
      footerHtml: v.footerHtml,
      countersignerName: v.countersignerName,
      countersignerSignatureKey: v.countersignerSignatureKey,
      marginTop: v.marginTop,
      marginBottom: v.marginBottom,
      marginLeft: v.marginLeft,
      marginRight: v.marginRight,
      showFirstPageMargins: v.showFirstPageMargins,
      signatureBlockHtml: v.signatureBlockHtml,
      signatoryPrefix: v.signatoryPrefix,
      signatorySuffix: v.signatorySuffix,
    });
  }, [tracking.values]);

  const isContentDirty = useMemo(() => {
    for (const key of tracking.dirtyFields) {
      if (!SETTINGS_FIELDS.has(key)) return true;
    }
    return false;
  }, [tracking.dirtyFields]);

  // Only fire markStale once the user has actually dirtied a content
  // field. The initial post-hydrate render flips contentSnapshot from
  // the empty defaults to the loaded values, which used to count as
  // a "change" and triggered the 20s idle auto-preview without any
  // edit.
  useEffect(() => {
    if (!hydrated) return;
    if (!isContentDirty) return;
    preview.markStale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentSnapshot, hydrated, isContentDirty]);

  // Idle countdown for the preview toolbar.
  const [idleRemaining, setIdleRemaining] = useState<number | null>(null);
  useEffect(() => {
    if (!preview.isStale) {
      setIdleRemaining(null);
      return;
    }
    let secondsLeft = 20;
    setIdleRemaining(secondsLeft);
    const tickId = window.setInterval(() => {
      secondsLeft -= 1;
      if (secondsLeft <= 0) {
        setIdleRemaining(null);
        window.clearInterval(tickId);
      } else {
        setIdleRemaining(secondsLeft);
      }
    }, 1000);
    return () => window.clearInterval(tickId);
  }, [preview.isStale, contentSnapshot]);

  const previewHash = preview.previewHash ?? agreement?.previewContentHash ?? null;

  const jurisdictionOk = jurisdictionComplete(
    tracking.values.jurisdictionKind,
    tracking.values.state,
    tracking.values.jurisdictionValue,
  );

  const canSave = useMemo(() => {
    if (saveResult.loading) return false;
    if (!tracking.isDirty) return false;
    if (!jurisdictionOk) return false; // incomplete jurisdiction
    if (!isContentDirty) return true; // settings-only change
    if (agreement?.previewContentHash == null) return true; // never previewed
    if (preview.status !== "ready") return false;
    if (preview.isStale) return false;
    return true;
  }, [
    saveResult.loading,
    tracking.isDirty,
    jurisdictionOk,
    isContentDirty,
    agreement?.previewContentHash,
    preview.status,
    preview.isStale,
  ]);

  // Why is the save button disabled? Surfaced via tooltip on the
  // disabled buttons. Settings-only edits and never-previewed agreements
  // don't need the message; only narrate the preview-gating cases.
  const saveDisabledReason = useMemo<string | null>(() => {
    if (canSave) return null;
    if (saveResult.loading) return null;
    if (!tracking.isDirty) return null;
    if (!jurisdictionOk)
      return "Complete the jurisdiction fields before saving.";
    if (!isContentDirty) return null;
    if (agreement?.previewContentHash == null) return null;
    if (preview.status === "generating")
      return "Generating preview… save unlocks when it's ready.";
    if (preview.isStale || preview.status === "stale")
      return "Refresh the preview before saving.";
    if (preview.status !== "ready")
      return "Refresh the preview before saving.";
    return null;
  }, [
    canSave,
    saveResult.loading,
    tracking.isDirty,
    jurisdictionOk,
    isContentDirty,
    agreement?.previewContentHash,
    preview.status,
    preview.isStale,
  ]);

  // Sanitized tenant-default previews, memoized: this component
  // re-renders on every setField edit and every preview idle-countdown
  // tick, and the defaults never change while mounted.
  const sanitizedDefaults = useMemo(
    () => ({
      header: sanitizeAgreementHtml(tenant?.defaultHeaderHtml),
      topLeft: sanitizeAgreementHtml(tenant?.defaultTopLeftHtml),
      topRight: sanitizeAgreementHtml(tenant?.defaultTopRightHtml),
      footer: sanitizeAgreementHtml(tenant?.defaultFooterHtml),
      signatureBlock: sanitizeAgreementHtml(tenant?.defaultSignatureBlockHtml),
    }),
    [tenant],
  );

  async function handleSave(thenGoBack: boolean) {
    if (!agreement) return;
    const v = tracking.values;
    try {
      const result = await save({
        variables: {
          id: agreement.id,
          name: v.name,
          agreementType: v.agreementType,
          agreementTypeId: v.agreementTypeId,
          jurisdictionKind: v.jurisdictionKind,
          state: v.state || null,
          jurisdictionValue: v.jurisdictionValue || null,
          isActive: v.isActive,
          agreementHtml: v.agreementHtml,
          simpleHtml: v.simpleHtml,
          headerHtml: v.headerHtml,
          topLeftHtml: v.topLeftHtml,
          topRightHtml: v.topRightHtml,
          footerHtml: v.footerHtml,
          countersignerName: v.countersignerName,
          countersignerSignatureKey: v.countersignerSignatureKey,
          marginTop: v.marginTop,
          marginBottom: v.marginBottom,
          marginLeft: v.marginLeft,
          marginRight: v.marginRight,
          showFirstPageMargins: v.showFirstPageMargins,
          signatureBlockHtml: v.signatureBlockHtml,
          signatoryPrefix: v.signatoryPrefix,
          signatorySuffix: v.signatorySuffix,
          previewContentHash: isContentDirty ? previewHash : null,
        },
      });
      const updated = result.data?.updateAgreement as AgreementForEdit | undefined;
      if (updated) {
        tracking.markSaved(valuesFromAgreement({ ...agreement, ...updated }));
      }
      toast.push({
        kind: "success",
        title: `${updated?.name ?? agreement.name} saved`,
        description: thenGoBack
          ? "Returning to your agreements list."
          : isContentDirty
            ? "Document and design changes are live for new bookings."
            : "Identity changes are live for new bookings.",
      });
      if (thenGoBack) navigate("/settings/agreements");
    } catch (e) {
      const message = e instanceof Error ? e.message : "Unknown error";
      const isStale = message.includes("Preview is out of date");
      toast.push({
        kind: "error",
        title: isStale
          ? "Preview is out of date"
          : `Couldn't save ${agreement.name}`,
        description: isStale
          ? "Refresh the preview, then try Save again."
          : message,
      });
    }
  }

  if (loading && !agreement) {
    return (
      <div className="px-7 py-10 text-ink-4 text-[13px]">Loading agreement…</div>
    );
  }
  if (error || !agreement || !tenant) {
    return (
      <div className="px-7 py-10 text-rust text-[13px]">
        Couldn't load agreement: {error?.message ?? "not found"}
      </div>
    );
  }

  const v = tracking.values;
  const setField = tracking.setField;

  const overrides = {
    header: v.headerHtml != null,
    topLeft: v.topLeftHtml != null,
    topRight: v.topRightHtml != null,
    footer: v.footerHtml != null,
    margins:
      v.marginTop != null ||
      v.marginBottom != null ||
      v.marginLeft != null ||
      v.marginRight != null,
    countersigner: v.countersignerName != null || v.countersignerSignatureKey != null,
    signatureBlock: v.signatureBlockHtml != null,
  };

  return (
    <div className="min-h-full">
      {/* Sticky header bar */}
      <div className="hair-b border-b sticky top-[var(--banner-h)] z-20 bg-paper/90 backdrop-blur">
        <div className="h-14 px-7 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/settings/agreements"
              className="text-ink-4 hover:text-ink-2 flex items-center gap-1.5 text-[12.5px] flex-shrink-0"
            >
              <ChevronLeft size={14} strokeWidth={2} />
              Agreements
            </Link>
            <span className="text-ink-5 flex-shrink-0">›</span>
            <span className="text-ink font-medium text-[13.5px] truncate">
              {v.name || agreement.name}
            </span>
            <span className="inline-flex items-center gap-1 text-[10.5px] text-ink-4 hair border rounded-full px-2 py-0.5 flex-shrink-0">
              {v.jurisdictionKind === "national"
                ? "National"
                : v.state || v.jurisdictionKind}
              {v.jurisdictionValue ? ` / ${v.jurisdictionValue}` : ""}
            </span>
            <StatusPill status={preview.status} isStale={preview.isStale} />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {tracking.dirtyCount > 0 && (
              <span className="text-[12px] text-ink-4 mr-2">
                {tracking.dirtyCount} unsaved
              </span>
            )}
            <CloneAction
              agreementId={agreement.id}
              agreementName={agreement.name}
              hasUnsavedChanges={tracking.isDirty}
            />
            <button
              type="button"
              onClick={tracking.reset}
              disabled={!tracking.isDirty || saveResult.loading}
              className="px-3 py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={preview.triggerPreview}
              disabled={preview.status === "generating"}
              className="px-3 py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw size={12} strokeWidth={2} />
              Refresh preview
            </button>
            <Tooltip label={saveDisabledReason} side="bottom">
              <button
                type="button"
                onClick={() => handleSave(false)}
                disabled={!canSave}
                className="px-3 py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3 disabled:bg-paper-2 disabled:text-ink-5 disabled:border-ink-6 disabled:cursor-not-allowed"
              >
                Save &amp; keep editing
              </button>
            </Tooltip>
            <Tooltip label={saveDisabledReason} side="bottom">
              <button
                type="button"
                onClick={() => handleSave(true)}
                disabled={!canSave}
                className="px-3.5 py-1.5 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium hover:bg-tide-d disabled:bg-ink-6 disabled:cursor-not-allowed"
              >
                Save &amp; go back
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Three-column layout */}
      <div className="grid grid-cols-[200px_1fr_460px] min-h-[calc(100vh-3.5rem)]">
        <OutlineRail active={active} onSelect={setActive} overrides={overrides} />

        {/* Editor pane */}
        <div className="bg-paper-2/40 overflow-y-auto">
          {/* Body editors stay mounted (with hidden) to preserve undo/cursor state */}
          <div
            className={`h-full ${active === "body" ? "block" : "hidden"}`}
            aria-hidden={active !== "body"}
          >
            <BodyEditorWrapper
              title="Full agreement"
              description="Embedded in the executed PDF"
              value={v.agreementHtml}
              onChange={(next) => setField("agreementHtml", next)}
            />
          </div>
          <div
            className={`h-full ${active === "simple" ? "block" : "hidden"}`}
            aria-hidden={active !== "simple"}
          >
            <BodyEditorWrapper
              title="Simple terms"
              description="Short summary shown inline during the booking flow"
              value={v.simpleHtml}
              onChange={(next) => setField("simpleHtml", next)}
            />
          </div>

          {active !== "body" && active !== "simple" && (
            <div className="px-7 py-7 max-w-[640px] space-y-6">
              {active === "header" && (
                <DocumentDesignOverridePanel
                  title="Header"
                  description="Page 1 only. Pages 2+ show a simplified header."
                  isOverriding={v.headerHtml != null}
                  onToggle={(on) =>
                    setField(
                      "headerHtml",
                      on ? tenant.defaultHeaderHtml ?? "" : null,
                    )
                  }
                  defaultPreview={
                    tenant.defaultHeaderHtml ? (
                      <div
                        className="text-[12.5px] [&_img]:max-w-full [&_img]:h-auto [&_img]:block [&_img]:mx-auto"
                        dangerouslySetInnerHTML={{
                          __html: sanitizedDefaults.header,
                        }}
                      />
                    ) : undefined
                  }
                >
                  <HeaderZone
                    value={v.headerHtml ?? ""}
                    onChange={(next) => setField("headerHtml", next)}
                  />
                </DocumentDesignOverridePanel>
              )}

              {active === "top-left" && (
                <DocumentDesignOverridePanel
                  title="Top-left margin"
                  description="Runs in the top-left corner from page 2 onward."
                  isOverriding={v.topLeftHtml != null}
                  onToggle={(on) =>
                    setField(
                      "topLeftHtml",
                      on ? tenant.defaultTopLeftHtml ?? "" : null,
                    )
                  }
                  defaultPreview={
                    tenant.defaultTopLeftHtml ? (
                      <div
                        className="text-[12.5px] [&_img]:max-w-full [&_img]:h-auto [&_img]:block [&_img]:mx-auto"
                        dangerouslySetInnerHTML={{
                          __html: sanitizedDefaults.topLeft,
                        }}
                      />
                    ) : undefined
                  }
                >
                  <DocumentDesignEditor
                    value={v.topLeftHtml ?? ""}
                    onChange={(next) => setField("topLeftHtml", next)}
                    height={220}
                    uploadType="top_left"
                  />
                </DocumentDesignOverridePanel>
              )}

              {active === "top-right" && (
                <DocumentDesignOverridePanel
                  title="Top-right margin"
                  description="Runs in the top-right corner from page 2 onward."
                  isOverriding={v.topRightHtml != null}
                  onToggle={(on) =>
                    setField(
                      "topRightHtml",
                      on ? tenant.defaultTopRightHtml ?? "" : null,
                    )
                  }
                  defaultPreview={
                    tenant.defaultTopRightHtml ? (
                      <div
                        className="text-[12.5px] [&_img]:max-w-full [&_img]:h-auto [&_img]:block [&_img]:mx-auto"
                        dangerouslySetInnerHTML={{
                          __html: sanitizedDefaults.topRight,
                        }}
                      />
                    ) : undefined
                  }
                >
                  <DocumentDesignEditor
                    value={v.topRightHtml ?? ""}
                    onChange={(next) => setField("topRightHtml", next)}
                    height={220}
                    uploadType="top_right"
                  />
                </DocumentDesignOverridePanel>
              )}

              {active === "footer" && (
                <DocumentDesignOverridePanel
                  title="Bottom-left margin"
                  description="Runs in the bottom-left corner of every page. Bottom-right is always the booking ID."
                  isOverriding={v.footerHtml != null}
                  onToggle={(on) =>
                    setField(
                      "footerHtml",
                      on ? tenant.defaultFooterHtml ?? "" : null,
                    )
                  }
                  defaultPreview={
                    tenant.defaultFooterHtml ? (
                      <div
                        className="text-[12.5px] [&_img]:max-w-full [&_img]:h-auto [&_img]:block [&_img]:mx-auto"
                        dangerouslySetInnerHTML={{
                          __html: sanitizedDefaults.footer,
                        }}
                      />
                    ) : undefined
                  }
                >
                  <FooterZone
                    value={v.footerHtml ?? ""}
                    onChange={(next) => setField("footerHtml", next)}
                  />
                </DocumentDesignOverridePanel>
              )}

              {active === "margins" && (
                <DocumentDesignOverridePanel
                  title="Margins"
                  description="Inches. Applied to every page. Includes first-page margin visibility."
                  isOverriding={overrides.margins}
                  onToggle={(on) => {
                    if (on) {
                      tracking.setValues({
                        ...v,
                        marginTop: tenant.defaultMarginTop ?? 0.75,
                        marginBottom: tenant.defaultMarginBottom ?? 0.75,
                        marginLeft: tenant.defaultMarginLeft ?? 0.5,
                        marginRight: tenant.defaultMarginRight ?? 0.5,
                        showFirstPageMargins:
                          tenant.defaultShowFirstPageMargins ?? false,
                      });
                    } else {
                      tracking.setValues({
                        ...v,
                        marginTop: null,
                        marginBottom: null,
                        marginLeft: null,
                        marginRight: null,
                        showFirstPageMargins: null,
                      });
                    }
                  }}
                  defaultPreview={
                    <div className="text-[12.5px] space-y-1">
                      <div className="num space-x-3">
                        <span>Top {(tenant.defaultMarginTop ?? 0.75).toFixed(2)}″</span>
                        <span>Bottom {(tenant.defaultMarginBottom ?? 0.75).toFixed(2)}″</span>
                        <span>Left {(tenant.defaultMarginLeft ?? 0.5).toFixed(2)}″</span>
                        <span>Right {(tenant.defaultMarginRight ?? 0.5).toFixed(2)}″</span>
                      </div>
                      <div className="text-ink-4">
                        First-page margin content:{" "}
                        <span className="text-ink-2 font-medium">
                          {tenant.defaultShowFirstPageMargins ? "shown" : "hidden"}
                        </span>
                      </div>
                    </div>
                  }
                >
                  <MarginsZone
                    value={{
                      top: v.marginTop ?? 0.75,
                      bottom: v.marginBottom ?? 0.75,
                      left: v.marginLeft ?? 0.5,
                      right: v.marginRight ?? 0.5,
                    }}
                    onChange={(next) =>
                      tracking.setValues({
                        ...v,
                        marginTop: next.top,
                        marginBottom: next.bottom,
                        marginLeft: next.left,
                        marginRight: next.right,
                      })
                    }
                    showFirstPageMargins={v.showFirstPageMargins ?? false}
                    onChangeShowFirstPageMargins={(next) =>
                      setField("showFirstPageMargins", next)
                    }
                  />
                </DocumentDesignOverridePanel>
              )}

              {active === "countersigner" && (
                <DocumentDesignOverridePanel
                  title="Countersigner"
                  description="Name and signature pre-applied to every executed agreement."
                  isOverriding={overrides.countersigner}
                  onToggle={(on) => {
                    if (on) {
                      tracking.setValues({
                        ...v,
                        countersignerName:
                          tenant.defaultCountersignerName ?? "",
                        countersignerSignatureKey:
                          tenant.defaultCountersignerSignatureKey ?? "",
                      });
                    } else {
                      tracking.setValues({
                        ...v,
                        countersignerName: null,
                        countersignerSignatureKey: null,
                      });
                    }
                  }}
                  defaultPreview={
                    <div className="text-[12.5px]">
                      <div>{tenant.defaultCountersignerName ?? "—"}</div>
                      {tenant.defaultCountersignerSignatureKey && (
                        <div className="text-ink-4 mt-1">
                          Signature on file
                        </div>
                      )}
                    </div>
                  }
                >
                  <CountersignerZone
                    name={v.countersignerName ?? ""}
                    signatureKey={v.countersignerSignatureKey || null}
                    cdnBaseUrl={tenant.cdnBaseUrl}
                    onChangeName={(next) => setField("countersignerName", next)}
                    onChangeSignatureKey={(next) =>
                      setField("countersignerSignatureKey", next)
                    }
                  />
                </DocumentDesignOverridePanel>
              )}

              {active === "signature-block" && (
                <>
                  <DocumentDesignOverridePanel
                    title="Signature block"
                    description="Legal effect statement above the customer's signature."
                    isOverriding={v.signatureBlockHtml != null}
                    onToggle={(on) =>
                      setField(
                        "signatureBlockHtml",
                        on ? tenant.defaultSignatureBlockHtml ?? "" : null,
                      )
                    }
                    defaultPreview={
                      tenant.defaultSignatureBlockHtml ? (
                        <div
                          className="text-[12.5px] leading-relaxed [&_img]:max-w-full [&_img]:h-auto [&_img]:block [&_img]:mx-auto"
                          dangerouslySetInnerHTML={{
                            __html: sanitizedDefaults.signatureBlock,
                          }}
                        />
                      ) : undefined
                    }
                  >
                    <SignatureBlockZone
                      value={v.signatureBlockHtml ?? ""}
                      onChange={(next) => setField("signatureBlockHtml", next)}
                    />
                  </DocumentDesignOverridePanel>

                  <div className="mt-6">
                    <h4 className="text-[14px] font-medium mb-1">
                      Signatory wording
                    </h4>
                    <p className="text-[12px] text-ink-4 mb-3">
                      Prefix and suffix text around the customer's name in the
                      signature block. Changes here require a fresh preview
                      before saving.
                    </p>
                    <div className="bg-paper-hi rounded-lg shadow-card p-5 space-y-4">
                      <label className="block">
                        <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                          Prefix
                        </span>
                        <input
                          type="text"
                          value={v.signatoryPrefix}
                          onChange={(e) =>
                            setField("signatoryPrefix", e.target.value)
                          }
                          placeholder="Client"
                          className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
                        />
                        <span className="text-[10.5px] text-ink-5 mt-0.5 block">
                          Appears before the signature line, e.g. "Client" or
                          "Applicant".
                        </span>
                      </label>
                      <label className="block">
                        <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                          Suffix
                        </span>
                        <input
                          type="text"
                          value={v.signatorySuffix}
                          onChange={(e) =>
                            setField("signatorySuffix", e.target.value)
                          }
                          placeholder=""
                          className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
                        />
                        <span className="text-[10.5px] text-ink-5 mt-0.5 block">
                          Appears after the signature line. Leave blank for none.
                        </span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {active === "settings" && (
                <SettingsPanel
                  name={v.name}
                  agreementType={v.agreementType}
                  agreementTypeId={v.agreementTypeId}
                  jurisdictionKind={v.jurisdictionKind}
                  state={v.state}
                  jurisdictionValue={v.jurisdictionValue}
                  isActive={v.isActive}
                  onChangeName={(next) => setField("name", next)}
                  onChangeJurisdictionKind={(next) => setField("jurisdictionKind", next)}
                  onChangeState={(next) => setField("state", next)}
                  onChangeJurisdictionValue={(next) => setField("jurisdictionValue", next)}
                  onChangeActive={(next) => setField("isActive", next)}
                />
              )}
            </div>
          )}
        </div>

        {/* Live preview pane — pinned so the editor column can scroll past */}
        <div
          className="hidden lg:flex flex-col sticky self-start"
          style={{
            top: "calc(var(--banner-h) + 3.5rem)",
            height: "calc(100vh - var(--banner-h) - 3.5rem)",
          }}
        >
          <PreviewPane
            status={preview.status}
            isStale={preview.isStale}
            previewUrl={preview.previewUrl}
            generatedAt={preview.generatedAt}
            idleSecondsRemaining={idleRemaining}
            error={preview.error}
            pageInfo={`${agreement.name}`}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Wrapper that fills the editor pane with the body WYSIWYG. Keeping
 * this in its own component lets us mount the HugeRTE instance once
 * and toggle visibility via the parent — preserving undo stack,
 * cursor position, and scroll while users flip between panels.
 */
function BodyEditorWrapper({
  title,
  description,
  value,
  onChange,
}: {
  title: string;
  description: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="px-6 py-6 h-full">
      <div className="bg-paper-hi rounded-lg shadow-card h-full overflow-hidden">
        <div className="px-5 py-3.5 hair-b border-b flex items-center justify-between">
          <div>
            <div className="text-[13.5px] font-medium">{title}</div>
            <div className="text-[11.5px] text-ink-4 mt-0.5">{description}</div>
          </div>
        </div>
        <div className="p-4" style={{ height: "calc(100% - 60px)" }}>
          <BodyEditor value={value} onChange={onChange} />
        </div>
      </div>
    </div>
  );
}
